/**
 * App icon badging — macOS Dock / iOS Home Screen / Windows taskbar.
 *
 * Mirrors the "Unified Inbox" unread count onto the OS app icon when
 * BuzzBit X runs as an installed app (Safari "Add to Dock", desktop PWA),
 * the way native Mac apps badge their Dock icon.
 *
 * lib.dom types the Badging API as always-present, but runtime support
 * varies (Firefox and older WebKit lack it), so every call is feature-
 * checked and failures are swallowed — badging is never critical.
 */

export function isAppBadgeSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.setAppBadge === 'function' &&
    typeof navigator.clearAppBadge === 'function'
  );
}

/** Set (count > 0) or clear (count <= 0) the app icon badge. Safe anywhere. */
export async function updateAppBadge(count: number): Promise<void> {
  if (!isAppBadgeSupported()) return;
  try {
    if (count > 0) {
      await navigator.setAppBadge(count);
    } else {
      await navigator.clearAppBadge();
    }
  } catch {
    // Badging rejected (e.g. not running as an installed app) — non-critical.
  }
}
